import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Header from '@/components/layout/Header';
import { SanitizedHtml } from '@/components/SanitizedHtml';
import { ReflectionSection } from '@/components/learning/ReflectionSection';
import { AssessmentSection } from '@/components/learning/AssessmentSection';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  ArrowLeft,
  BookOpen,
  CheckCircle2,
  Play,
  Pause,
  Target,
  X,
  Trophy,
  Eye,
  Sparkles,
  Calendar,
  Flame,
  ArrowRight,
} from 'lucide-react';
import { BrandedLoading, BrandedSpinner, ErrorState } from '@/components/common';
import { userLearningPlanApi, userLessonApi } from '@/services/learningManagementApi';
import type {
  ActivePlanDashboardResponse,
  UserLessonResponse,
  UserLessonStatus,
} from '@/types/learningManagement';

const STATUS_CONFIG: Record<
  UserLessonStatus,
  { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: React.ReactNode }
> = {
  DRAFT: { label: 'Draft', variant: 'secondary', icon: <BookOpen className="w-3 h-3" /> },
  PLANNED: { label: 'Planned', variant: 'outline', icon: <Target className="w-3 h-3" /> },
  ACTIVE: { label: 'In progress', variant: 'default', icon: <Play className="w-3 h-3" /> },
  PAUSED: { label: 'Paused', variant: 'secondary', icon: <Pause className="w-3 h-3" /> },
  COMPLETED: { label: 'Completed', variant: 'default', icon: <CheckCircle2 className="w-3 h-3" /> },
  EXPIRED: { label: 'Expired', variant: 'destructive', icon: <X className="w-3 h-3" /> },
  ARCHIVED: { label: 'Archived', variant: 'secondary', icon: <X className="w-3 h-3" /> },
};

// ---------------------------------------------------------------------------
// Confetti CSS animation (injected once via <style>)
// ---------------------------------------------------------------------------
const CONFETTI_STYLES = `
@keyframes five-confetti-fall {
  0% { transform: translateY(-10vh) rotate(0deg); opacity: 1; }
  100% { transform: translateY(110vh) rotate(720deg); opacity: 0; }
}
@keyframes five-confetti-sway {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(15px); }
  75% { transform: translateX(-15px); }
}
@keyframes five-trophy-glow {
  0%, 100% { filter: drop-shadow(0 0 8px rgba(251, 191, 36, 0.4)); }
  50% { filter: drop-shadow(0 0 24px rgba(251, 191, 36, 0.8)); }
}
@keyframes five-gradient-shift {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}
`;

const CONFETTI_COLORS = [
  '#7c3aed', '#a78bfa', '#8b5cf6', '#c084fc', '#e879f9',
  '#f472b6', '#fb923c', '#facc15', '#34d399', '#22d3ee',
];

const ConfettiPiece: React.FC<{ index: number }> = ({ index }) => {
  const color = CONFETTI_COLORS[index % CONFETTI_COLORS.length];
  const left = `${Math.random() * 100}%`;
  const delay = `${Math.random() * 3}s`;
  const duration = `${2.5 + Math.random() * 2}s`;
  const size = 6 + Math.random() * 6;
  const shape = index % 3 === 0 ? 'rounded-full' : index % 3 === 1 ? 'rounded-sm' : '';

  return (
    <div
      className={`absolute ${shape}`}
      style={{
        left,
        top: '-10px',
        width: `${size}px`,
        height: `${size}px`,
        backgroundColor: color,
        animation: `five-confetti-fall ${duration} ${delay} linear forwards, five-confetti-sway ${duration} ${delay} ease-in-out infinite`,
        opacity: 0.9,
      }}
    />
  );
};

const ConfettiOverlay: React.FC = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none z-10">
    {Array.from({ length: 60 }).map((_, i) => (
      <ConfettiPiece key={i} index={i} />
    ))}
  </div>
);

// ---------------------------------------------------------------------------
// Celebration Section
// ---------------------------------------------------------------------------
const CelebrationSection: React.FC<{
  daysCompleted: number;
  totalDays: number;
  lessonsCompleted: number;
  followUpStatus?: 'NONE' | 'GENERATING' | 'READY';
  onStartNextPlan: () => void;
  onGenerateNewPlan: () => void;
  loadingNext: boolean;
}> = ({ daysCompleted, totalDays, lessonsCompleted, followUpStatus, onStartNextPlan, onGenerateNewPlan, loadingNext }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.5, ease: 'easeOut' }}
    className="relative"
  >
    <Card className="overflow-hidden border-2 border-violet-500/30 dark:border-violet-400/20">
      <ConfettiOverlay />
      <CardContent className="relative z-20 py-10 px-6 text-center space-y-6">
        {/* Trophy */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 200, damping: 12 }}
          className="flex justify-center"
        >
          <div
            className="w-24 h-24 rounded-full bg-gradient-to-br from-amber-400 via-yellow-300 to-amber-500 flex items-center justify-center"
            style={{ animation: 'five-trophy-glow 2s ease-in-out infinite' }}
          >
            <Trophy className="w-12 h-12 text-amber-900" />
          </div>
        </motion.div>

        {/* Heading */}
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="text-3xl sm:text-4xl font-extrabold"
          style={{
            background: 'linear-gradient(135deg, #7c3aed, #a78bfa, #c084fc, #e879f9)',
            backgroundSize: '200% 200%',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            animation: 'five-gradient-shift 4s ease infinite',
          }}
        >
          Congratulations!
        </motion.h2>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.45 }}
          className="text-muted-foreground max-w-md mx-auto"
        >
          You&apos;ve successfully completed your learning plan. Your dedication is inspiring!
        </motion.p>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55 }}
          className="flex flex-wrap items-center justify-center gap-6 py-4"
        >
          <div className="flex flex-col items-center gap-1 min-w-[80px]">
            <div className="w-12 h-12 rounded-xl bg-violet-500/10 dark:bg-violet-500/20 flex items-center justify-center">
              <Calendar className="w-6 h-6 text-violet-600 dark:text-violet-400" />
            </div>
            <span className="text-2xl font-bold text-foreground">{daysCompleted}/{totalDays}</span>
            <span className="text-xs text-muted-foreground">Days completed</span>
          </div>
          <div className="flex flex-col items-center gap-1 min-w-[80px]">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/10 dark:bg-emerald-500/20 flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
            </div>
            <span className="text-2xl font-bold text-foreground">{lessonsCompleted}</span>
            <span className="text-xs text-muted-foreground">Lessons completed</span>
          </div>
          <div className="flex flex-col items-center gap-1 min-w-[80px]">
            <div className="w-12 h-12 rounded-xl bg-amber-500/10 dark:bg-amber-500/20 flex items-center justify-center">
              <Flame className="w-6 h-6 text-amber-600 dark:text-amber-400" />
            </div>
            <span className="text-2xl font-bold text-foreground">100%</span>
            <span className="text-xs text-muted-foreground">Progress</span>
          </div>
        </motion.div>

        {/* Follow-up states */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
        >
          {followUpStatus === 'GENERATING' && (
            <div className="flex items-center justify-center gap-3 py-3 px-5 rounded-xl bg-amber-500/10 dark:bg-amber-500/15 border border-amber-500/20 mx-auto max-w-sm">
              <BrandedSpinner sizeClassName="h-5 w-5" className="shrink-0" />
              <p className="text-sm text-amber-800 dark:text-amber-200">
                Your next plan is being prepared...
              </p>
            </div>
          )}
          {followUpStatus === 'READY' && (
            <FollowUpReadyCard onStartNextPlan={onStartNextPlan} loadingNext={loadingNext} />
          )}
          {followUpStatus === 'NONE' && (
            <Button
              size="lg"
              onClick={onGenerateNewPlan}
              disabled={loadingNext}
              className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white shadow-lg shadow-violet-500/25"
            >
              {loadingNext ? (
                <BrandedSpinner sizeClassName="h-5 w-5" />
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Generate new plan
                </>
              )}
            </Button>
          )}
        </motion.div>
      </CardContent>
    </Card>
  </motion.div>
);

// ---------------------------------------------------------------------------
// Follow-up plan READY card
// ---------------------------------------------------------------------------
const FollowUpReadyCard: React.FC<{
  onStartNextPlan: () => void;
  loadingNext: boolean;
}> = ({ onStartNextPlan, loadingNext }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4 }}
  >
    <Card className="border-2 border-emerald-500/30 dark:border-emerald-400/20 bg-gradient-to-br from-emerald-500/5 to-violet-500/5 dark:from-emerald-500/10 dark:to-violet-500/10">
      <CardContent className="py-6 px-6 text-center space-y-4">
        <div className="flex justify-center">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/20">
            <Sparkles className="w-7 h-7 text-white" />
          </div>
        </div>
        <div>
          <h3 className="text-lg font-bold text-foreground">Your next learning plan is ready!</h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">
            Based on your progress, we&apos;ve prepared a personalized follow-up plan to continue your growth.
          </p>
        </div>
        <div className="flex items-center justify-center gap-3">
          <Button
            size="lg"
            onClick={onStartNextPlan}
            disabled={loadingNext}
            className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white shadow-lg shadow-emerald-500/25"
          >
            {loadingNext ? (
              <BrandedSpinner sizeClassName="h-5 w-5" />
            ) : (
              <>
                Start next plan
                <ArrowRight className="w-5 h-5 ml-2" />
              </>
            )}
          </Button>
          <Button variant="outline" size="lg" disabled title="Coming soon">
            Customize
          </Button>
        </div>
      </CardContent>
    </Card>
  </motion.div>
);

// ---------------------------------------------------------------------------
// Complete Plan Button
// ---------------------------------------------------------------------------
const CompletePlanButton: React.FC<{
  completing: boolean;
  onClick: () => void;
}> = ({ completing, onClick }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4 }}
  >
    <Card className="border-2 border-violet-500/30 dark:border-violet-400/20 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-violet-600/5 via-purple-600/5 to-fuchsia-600/5 dark:from-violet-600/10 dark:via-purple-600/10 dark:to-fuchsia-600/10" />
      <CardContent className="relative py-6 px-6 text-center space-y-4">
        <div className="flex justify-center">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-violet-400 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-500/25">
            <Trophy className="w-7 h-7 text-white" />
          </div>
        </div>
        <div>
          <h3 className="text-lg font-bold text-foreground">You did it! All content is complete.</h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">
            Mark your plan as completed and celebrate your achievement.
          </p>
        </div>
        <Button
          size="lg"
          onClick={onClick}
          disabled={completing}
          className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white shadow-lg shadow-violet-500/25"
        >
          {completing ? (
            <BrandedSpinner sizeClassName="h-5 w-5" />
          ) : (
            <>
              <CheckCircle2 className="w-5 h-5 mr-2" />
              Complete your plan
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  </motion.div>
);

// ---------------------------------------------------------------------------
// Completed Plan Header (replaces the default header when plan is COMPLETED)
// ---------------------------------------------------------------------------
const CompletedPlanHeader: React.FC<{
  title: string;
  daysCompleted: number;
  totalDays: number;
  completedAt?: string;
}> = ({ title, daysCompleted, totalDays, completedAt }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.05 }}
  >
    <Card className="overflow-hidden">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="text-2xl">{title}</CardTitle>
            {completedAt && (
              <p className="text-sm text-muted-foreground mt-1">
                Completed on {new Date(completedAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            )}
          </div>
          <Badge className="bg-emerald-600 hover:bg-emerald-700 text-white border-0 gap-1">
            <CheckCircle2 className="w-3 h-3" />
            Completed
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span className="font-medium text-muted-foreground">Overall progress</span>
            <span className="font-semibold text-emerald-600">100%</span>
          </div>
          <div className="h-2 rounded-full bg-muted overflow-hidden">
            <div className="h-full w-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-500" />
          </div>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <span className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            {daysCompleted} of {totalDays} days completed
          </span>
          <span className="flex items-center gap-2">
            <Trophy className="w-4 h-4 text-amber-500" />
            Plan finished
          </span>
        </div>
      </CardContent>
    </Card>
  </motion.div>
);

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
export const ActivePlanDashboardPage: React.FC = () => {
  const { userEmail: encodedEmail } = useParams<{ userEmail: string }>();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState<ActivePlanDashboardResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [viewingLesson, setViewingLesson] = useState<UserLessonResponse | null>(null);

  // New states for plan completion flow
  const [completing, setCompleting] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [loadingNext, setLoadingNext] = useState(false);
  const followUpPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Post-completion flow: reflection → assessment → next plan
  const [reflectionDone, setReflectionDone] = useState(false);
  const [assessmentDone, setAssessmentDone] = useState(false);

  const userEmail = encodedEmail ? decodeURIComponent(encodedEmail) : null;

  const fetchDashboard = useCallback(async () => {
    if (!userEmail) return;
    try {
      setError(null);
      const data = await userLearningPlanApi.getDashboard(userEmail);
      setDashboard(data);
    } catch (e) {
      setError(e instanceof Error ? e : new Error('Failed to load dashboard'));
      setDashboard(null);
    } finally {
      setIsLoading(false);
    }
  }, [userEmail]);

  useEffect(() => {
    if (userEmail) fetchDashboard();
    else {
      setError(new Error('User email is required'));
      setIsLoading(false);
    }
  }, [userEmail, fetchDashboard]);

  // Poll for follow-up plan status when GENERATING
  useEffect(() => {
    const status = dashboard?.followUpPlanStatus;
    if (status === 'GENERATING') {
      followUpPollRef.current = setInterval(async () => {
        await fetchDashboard();
      }, 10_000);
    }
    return () => {
      if (followUpPollRef.current) {
        clearInterval(followUpPollRef.current);
        followUpPollRef.current = null;
      }
    };
  }, [dashboard?.followUpPlanStatus, fetchDashboard]);

  // Auto-show celebration if plan is already COMPLETED on load
  useEffect(() => {
    if (dashboard?.activePlan?.status === 'COMPLETED' && !showCelebration) {
      setShowCelebration(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboard?.activePlan?.status]);

  // ---- Handlers ----

  const handleCompletePlan = async () => {
    const planId = dashboard?.activePlan?.id;
    if (!planId) return;
    setCompleting(true);
    try {
      await userLearningPlanApi.complete(planId);
      setShowCelebration(true);
      await fetchDashboard();
    } catch {
      // Silently handled — dashboard stays as-is
    } finally {
      setCompleting(false);
    }
  };

  const handleStartNextPlan = async () => {
    setLoadingNext(true);
    try {
      // Refresh dashboard — backend will activate the follow-up plan
      await fetchDashboard();
      setShowCelebration(false);
    } finally {
      setLoadingNext(false);
    }
  };

  const handleGenerateNewPlan = () => {
    navigate('/onboarding');
  };

  const handleLessonStart = async (lesson: UserLessonResponse) => {
    if (!lesson.id) return;
    setActionLoading(lesson.id);
    try {
      const updated = await userLessonApi.start(lesson.id);
      setViewingLesson(updated);
      await fetchDashboard();
    } catch {
      setActionLoading(null);
    } finally {
      setActionLoading(null);
    }
  };

  const handleLessonView = async (lesson: UserLessonResponse) => {
    if (!lesson.id) return;
    setActionLoading(lesson.id);
    try {
      const updated = await userLessonApi.view(lesson.id);
      setViewingLesson(updated);
      await fetchDashboard();
    } catch {
      setActionLoading(null);
    } finally {
      setActionLoading(null);
    }
  };

  const handleLessonComplete = async (lesson: UserLessonResponse) => {
    if (!lesson.id) return;
    setActionLoading(lesson.id);
    try {
      await userLessonApi.complete(lesson.id);
      await fetchDashboard();
    } catch {
      setActionLoading(null);
    } finally {
      setActionLoading(null);
    }
  };

  const handleLessonDismiss = async (lesson: UserLessonResponse) => {
    if (!lesson.id) return;
    setActionLoading(lesson.id);
    try {
      await userLessonApi.dismiss(lesson.id);
      await fetchDashboard();
    } catch {
      setActionLoading(null);
    } finally {
      setActionLoading(null);
    }
  };

  // ---- Derived state ----

  const plan = dashboard?.activePlan;
  const todaysContent = dashboard?.todaysContent;
  const todaysLessons = dashboard?.todaysLessons ?? [];
  const progress = dashboard?.overallProgress ?? 0;
  const currentDay = dashboard?.currentDayNumber ?? 1;
  const totalDays = dashboard?.totalDays ?? 7;
  const daysCompleted = dashboard?.daysCompleted ?? 0;
  const daysRemaining = dashboard?.daysRemaining ?? 6;

  const allTodaysLessonsDone = todaysLessons.length > 0 && todaysLessons.every(
    (l) => l.status === 'COMPLETED' || l.status === 'ARCHIVED' || l.status === 'EXPIRED',
  );
  const isPlanCompleted = plan?.status === 'COMPLETED';
  const isEligibleForCompletion =
    !isPlanCompleted &&
    !showCelebration &&
    (progress >= 100 || (allTodaysLessonsDone && daysRemaining === 0));

  const completedLessonCount = todaysLessons.filter((l) => l.status === 'COMPLETED').length;

  // ---- Error / Loading gates ----

  if (!userEmail) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="flex items-center justify-center min-h-[calc(100vh-8rem)]">
          <ErrorState error={new Error('User email is required')} onRetry={() => navigate(-1)} />
        </main>
        <Footer />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-primary/5 to-violet-500/10 dark:from-slate-950 dark:via-primary/10 dark:to-violet-950/30">
        <Header />
        <main className="flex items-center justify-center min-h-[calc(100vh-8rem)] px-4">
          <BrandedLoading message="Loading your learning dashboard..." iconSize="md" />
        </main>
        <Footer />
      </div>
    );
  }

  if (error && !dashboard) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="flex items-center justify-center min-h-[calc(100vh-8rem)]">
          <ErrorState error={error} onRetry={fetchDashboard} />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-slate-50 to-primary/5 dark:via-slate-950 dark:to-primary/10">
      {/* Inject confetti keyframes */}
      <style>{CONFETTI_STYLES}</style>
      <Header />
      <main className="py-8 pt-20 sm:py-12 sm:pt-24">
        <div className="container max-w-4xl mx-auto px-4 sm:px-6 space-y-8">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-4"
          >
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </motion.div>

          {!plan ? (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-2xl border border-dashed border-border bg-muted/30 p-12 text-center"
            >
              <BookOpen className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <h2 className="text-xl font-semibold text-foreground mb-2">No active learning plan</h2>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Start your journey by completing onboarding to get a personalized learning plan.
              </p>
              <Button onClick={() => navigate('/onboarding')}>Go to onboarding</Button>
            </motion.div>
          ) : (
            <>
              {/* ============================================================ */}
              {/* CELEBRATION VIEW (after completing plan) */}
              {/* ============================================================ */}
              <AnimatePresence>
                {showCelebration && (
                  <>
                    <CompletedPlanHeader
                      title={plan.title}
                      daysCompleted={daysCompleted}
                      totalDays={totalDays}
                      completedAt={plan.updatedAt}
                    />
                    <CelebrationSection
                      daysCompleted={daysCompleted}
                      totalDays={totalDays}
                      lessonsCompleted={completedLessonCount}
                      followUpStatus={reflectionDone && assessmentDone ? dashboard?.followUpPlanStatus : undefined}
                      onStartNextPlan={handleStartNextPlan}
                      onGenerateNewPlan={handleGenerateNewPlan}
                      loadingNext={loadingNext}
                    />

                    {/* Post-completion flow: Reflection → Assessment → Next Plan */}
                    {!reflectionDone && (
                      <div className="mt-4">
                        <ReflectionSection
                          userId={userEmail ?? ''}
                          learningPathId={dashboard?.learningPath?.id ?? ''}
                          planId={plan.id}
                          planTitle={plan.title}
                          onComplete={() => setReflectionDone(true)}
                          onSkip={() => setReflectionDone(true)}
                        />
                      </div>
                    )}

                    {reflectionDone && !assessmentDone && (() => {
                      // Find assessment ID from the plan's final_assessment_id or last day's assessment_id
                      const modules = plan.learningModules ?? (plan as unknown as Record<string, unknown>).learning_modules as unknown[];
                      let assessId: string | undefined;
                      if (Array.isArray(modules) && modules.length > 0) {
                        const mod = modules[0] as Record<string, unknown>;
                        assessId = (mod.finalAssessmentId ?? mod.final_assessment_id) as string | undefined;
                        if (!assessId) {
                          const daily = (mod.dailyContents ?? mod.daily_contents) as Array<Record<string, unknown>> | undefined;
                          if (daily && daily.length > 0) {
                            const lastDay = daily[daily.length - 1];
                            assessId = (lastDay.assessmentId ?? lastDay.assessment_id) as string | undefined;
                          }
                        }
                      }
                      if (!assessId) {
                        // No assessment available — skip
                        setTimeout(() => setAssessmentDone(true), 0);
                        return null;
                      }
                      return (
                        <div className="mt-4">
                          <AssessmentSection
                            userId={userEmail ?? ''}
                            assessmentId={assessId}
                            onComplete={() => setAssessmentDone(true)}
                            onSkip={() => setAssessmentDone(true)}
                          />
                        </div>
                      );
                    })()}
                  </>
                )}
              </AnimatePresence>

              {/* ============================================================ */}
              {/* ACTIVE PLAN VIEW (normal daily view) */}
              {/* ============================================================ */}
              {!showCelebration && (
                <>
                  {/* Plan header & progress */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05 }}
                  >
                    <Card className="overflow-hidden">
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <CardTitle className="text-2xl">{plan.title}</CardTitle>
                            <p className="text-sm text-muted-foreground mt-1">
                              Day {currentDay} of {totalDays}
                            </p>
                          </div>
                          <Badge variant={plan.status === 'IN_PROGRESS' ? 'default' : 'secondary'}>
                            {plan.status}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="font-medium text-muted-foreground">Overall progress</span>
                            <span className="font-semibold text-primary">{Math.round(progress)}%</span>
                          </div>
                          <div className="h-2 rounded-full bg-muted overflow-hidden">
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${progress}%` }}
                              transition={{ duration: 0.6, ease: 'easeOut' }}
                              className="h-full rounded-full bg-gradient-to-r from-primary to-violet-600"
                            />
                          </div>
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                            {daysCompleted} days completed
                          </span>
                          <span className="flex items-center gap-2">
                            <Trophy className="w-4 h-4 text-amber-500" />
                            {daysRemaining} remaining
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>

                  {/* Today's content */}
                  {todaysContent && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 }}
                    >
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-lg flex items-center gap-2">
                            <Target className="w-5 h-5" />
                            Today: {todaysContent.title}
                          </CardTitle>
                          {todaysContent.description && (
                            <p className="text-sm text-muted-foreground mt-1">{todaysContent.description}</p>
                          )}
                          {todaysContent.learningObjectives && todaysContent.learningObjectives.length > 0 && (
                            <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                              {todaysContent.learningObjectives.map((obj, i) => (
                                <li key={i} className="flex items-center gap-2">
                                  <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                                  {obj}
                                </li>
                              ))}
                            </ul>
                          )}
                        </CardHeader>
                      </Card>
                    </motion.div>
                  )}

                  {/* Today's lessons */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                  >
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Today&apos;s lessons</CardTitle>
                        <p className="text-sm text-muted-foreground">
                          {todaysLessons.length} lesson{todaysLessons.length !== 1 ? 's' : ''}
                        </p>
                      </CardHeader>
                      <CardContent>
                        {todaysLessons.length === 0 ? (
                          <p className="text-muted-foreground text-sm py-8 text-center">
                            No lessons scheduled for today.
                          </p>
                        ) : (
                          <ul className="space-y-4">
                            {todaysLessons.map((lesson) => {
                              const cfg = STATUS_CONFIG[lesson.status] ?? STATUS_CONFIG.PLANNED;
                              const isLessonLoading = actionLoading === lesson.id;
                              const canStart = lesson.status === 'DRAFT' || lesson.status === 'PLANNED';
                              const canComplete =
                                lesson.status === 'ACTIVE' || lesson.status === 'PLANNED' || lesson.status === 'DRAFT';
                              const isCompleted = lesson.status === 'COMPLETED';
                              return (
                                <li
                                  key={lesson.id}
                                  className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-xl border border-border bg-card hover:bg-muted/30 transition-colors"
                                >
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <h4 className="font-semibold text-foreground">{lesson.title}</h4>
                                      <Badge variant={cfg.variant} className="gap-1">
                                        {cfg.icon}
                                        {cfg.label}
                                      </Badge>
                                    </div>
                                    {lesson.description && (
                                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                                        {lesson.description}
                                      </p>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    {(lesson.content?.body || lesson.description) && (
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleLessonView(lesson)}
                                        disabled={isLessonLoading}
                                      >
                                        {actionLoading === lesson.id ? (
                                          <BrandedSpinner sizeClassName="h-4 w-4" />
                                        ) : (
                                          <>
                                            <Eye className="w-4 h-4 mr-1" />
                                            View
                                          </>
                                        )}
                                      </Button>
                                    )}
                                    {canStart && (
                                      <Button
                                        size="sm"
                                        onClick={() => handleLessonStart(lesson)}
                                        disabled={isLessonLoading}
                                      >
                                        {isLessonLoading ? (
                                          <BrandedSpinner sizeClassName="h-4 w-4" />
                                        ) : (
                                          <>
                                            <Play className="w-4 h-4 mr-1" />
                                            Start
                                          </>
                                        )}
                                      </Button>
                                    )}
                                    {lesson.status === 'ACTIVE' && (
                                      <>
                                        <Button
                                          size="sm"
                                          onClick={() => handleLessonComplete(lesson)}
                                          disabled={isLessonLoading}
                                        >
                                          {isLessonLoading ? (
                                            <BrandedSpinner sizeClassName="h-4 w-4" />
                                          ) : (
                                            <>
                                              <CheckCircle2 className="w-4 h-4 mr-1" />
                                              Complete
                                            </>
                                          )}
                                        </Button>
                                      </>
                                    )}
                                    {canComplete && !canStart && !isCompleted && lesson.status !== 'ACTIVE' && (
                                      <Button
                                        size="sm"
                                        onClick={() => handleLessonComplete(lesson)}
                                        disabled={isLessonLoading}
                                      >
                                        {isLessonLoading ? (
                                          <BrandedSpinner sizeClassName="h-4 w-4" />
                                        ) : (
                                          <>
                                            <CheckCircle2 className="w-4 h-4 mr-1" />
                                            Complete
                                          </>
                                        )}
                                      </Button>
                                    )}
                                    {!['COMPLETED', 'ARCHIVED', 'EXPIRED'].includes(lesson.status) && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleLessonDismiss(lesson)}
                                        disabled={isLessonLoading}
                                      >
                                        <X className="w-4 h-4" />
                                      </Button>
                                    )}
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>

                  {/* Complete Plan button — shown when all content is done */}
                  {isEligibleForCompletion && (
                    <CompletePlanButton completing={completing} onClick={handleCompletePlan} />
                  )}

                  {/* Follow-up plan status (for active/in-progress plans) */}
                  {!isEligibleForCompletion && dashboard?.followUpPlanStatus === 'GENERATING' && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 flex items-center gap-3"
                    >
                      <BrandedSpinner sizeClassName="h-5 w-5" className="shrink-0" />
                      <p className="text-sm text-amber-800 dark:text-amber-200">
                        Your follow-up plan is being generated. This may take a few minutes.
                      </p>
                    </motion.div>
                  )}

                  {!isEligibleForCompletion && dashboard?.followUpPlanStatus === 'READY' && (
                    <FollowUpReadyCard onStartNextPlan={handleStartNextPlan} loadingNext={loadingNext} />
                  )}
                </>
              )}
            </>
          )}

          {/* Lesson content dialog */}
          <Dialog open={!!viewingLesson} onOpenChange={(open) => !open && setViewingLesson(null)}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              {viewingLesson && (
                <>
                  <DialogHeader>
                    <DialogTitle>{viewingLesson.title}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    {viewingLesson.description && (
                      <p className="text-muted-foreground">{viewingLesson.description}</p>
                    )}
                    {viewingLesson.content?.body && (
                      <div className="prose prose-sm dark:prose-invert max-w-none">
                        {viewingLesson.content.format === 'MARKDOWN' ? (
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {viewingLesson.content.body}
                          </ReactMarkdown>
                        ) : viewingLesson.content.format === 'HTML' ? (
                          <SanitizedHtml
                            html={viewingLesson.content.body}
                            className="prose prose-sm dark:prose-invert max-w-none"
                          />
                        ) : (
                          <pre className="whitespace-pre-wrap font-sans text-sm">
                            {viewingLesson.content.body}
                          </pre>
                        )}
                      </div>
                    )}
                    {viewingLesson.content?.sections &&
                      viewingLesson.content.sections.length > 0 && (
                        <div className="space-y-4">
                          {viewingLesson.content.sections.map((section, i) => (
                            <div key={i}>
                              {section.title && (
                                <h4 className="font-semibold text-foreground mb-2">
                                  {section.title}
                                </h4>
                              )}
                              {section.body && (
                                <div className="prose prose-sm dark:prose-invert max-w-none">
                                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                    {section.body}
                                  </ReactMarkdown>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    {!viewingLesson.content?.body &&
                      !viewingLesson.content?.sections?.length &&
                      viewingLesson.description && (
                        <p className="text-muted-foreground">{viewingLesson.description}</p>
                      )}
                    {viewingLesson.status !== 'COMPLETED' && viewingLesson.id && (
                      <div className="pt-4 flex gap-2">
                        <Button
                          size="sm"
                          onClick={async () => {
                            await handleLessonComplete(viewingLesson);
                            setViewingLesson(null);
                          }}
                          disabled={actionLoading === viewingLesson.id}
                        >
                          {actionLoading === viewingLesson.id ? (
                            <BrandedSpinner sizeClassName="h-4 w-4" />
                          ) : (
                            <>
                              <CheckCircle2 className="w-4 h-4 mr-1" />
                              Mark complete
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </main>
      <Footer />
    </div>
  );
};
